import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HardDrive, Trash2, Loader2, Clock, CheckCircle, AlertTriangle, Play, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import StorageSettings from './StorageSettings';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStorage = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupSettings, setCleanupSettings] = useState(null);
  const [intervalHours, setIntervalHours] = useState(1);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadCleanupSettings();
  }, [token]);

  const loadCleanupSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/cleanup/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setCleanupSettings(res.data);
      setIntervalHours(res.data.interval_hours || 1);
      setAutoEnabled(res.data.auto_cleanup_enabled !== false);
    } catch { }
  };

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Nettoyage terminé', description: res.data.message });
      loadCleanupSettings();
    } catch {
      toast({ title: 'Erreur', description: 'Erreur lors du nettoyage', variant: 'destructive' });
    }
    setCleaningUp(false);
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/cleanup/settings`, {
        interval_hours: intervalHours,
        auto_cleanup_enabled: autoEnabled
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Paramètres sauvegardés' });
      loadCleanupSettings();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
    setSavingSettings(false);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dateStr = typeof d === 'string' && !d.endsWith('Z') && !d.includes('+') ? d + 'Z' : d;
    return new Date(dateStr).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 o';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <Toaster />
      <NavigationMenu />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/admin/dashboard?tab=settings" className="text-blue-400 hover:text-blue-300 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <HardDrive className="w-6 h-6 text-blue-400" />
            <h1 className="text-xl font-semibold text-white">Stockage des fichiers</h1>
          </div>
          
          <StorageSettings token={token} toast={toast} />

          {/* Cleanup Section */}
          <div className="bg-slate-800/40 border border-blue-500/20 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-orange-500/20 rounded-lg">
                <Trash2 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Nettoyage automatique des fichiers expirés</h3>
                <p className="text-sm text-blue-300/60">Supprime les fichiers et libère l'espace sur le stockage configuré (local, Wasabi, SFTP)</p>
              </div>
            </div>

            {/* Status Cards */}
            {cleanupSettings && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-900/50 border border-blue-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{cleanupSettings.total_active || 0}</p>
                  <p className="text-xs text-blue-300/50 mt-1">Fichiers actifs</p>
                </div>
                <div className="bg-slate-900/50 border border-blue-500/10 rounded-lg p-3 text-center">
                  <p className={`text-2xl font-bold ${cleanupSettings.pending_cleanup > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                    {cleanupSettings.pending_cleanup || 0}
                  </p>
                  <p className="text-xs text-blue-300/50 mt-1">En attente de nettoyage</p>
                </div>
                <div className="bg-slate-900/50 border border-blue-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-300">{cleanupSettings.total_cleaned || 0}</p>
                  <p className="text-xs text-blue-300/50 mt-1">Fichiers nettoyés</p>
                </div>
                <div className="bg-slate-900/50 border border-blue-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-cyan-400">{formatSize(cleanupSettings.last_freed_bytes)}</p>
                  <p className="text-xs text-blue-300/50 mt-1">Dernière libération</p>
                </div>
              </div>
            )}

            {/* Last run info */}
            {cleanupSettings && (
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <div className="flex items-center gap-2 text-blue-300/60">
                  <Clock className="w-4 h-4" />
                  Dernier nettoyage : <span className="text-white font-medium">{formatDate(cleanupSettings.last_run)}</span>
                </div>
                <div className="flex items-center gap-2 text-blue-300/60">
                  {cleanupSettings.last_cleaned > 0
                    ? <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400">{cleanupSettings.last_cleaned} fichier(s) nettoyé(s)</span></>
                    : <><CheckCircle className="w-4 h-4 text-blue-400" /><span className="text-blue-400">Aucun fichier à nettoyer</span></>
                  }
                </div>
              </div>
            )}

            {/* Configuration */}
            <div className="border-t border-blue-500/10 pt-5 space-y-4" data-testid="cleanup-config">
              <h4 className="text-sm font-medium text-white flex items-center gap-2">
                <Settings className="w-4 h-4 text-blue-400" /> Configuration du cron
              </h4>
              
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoEnabled}
                    onChange={(e) => setAutoEnabled(e.target.checked)}
                    className="w-4 h-4 rounded bg-slate-700 border-blue-500/30 text-blue-500 focus:ring-blue-500"
                    data-testid="auto-cleanup-toggle"
                  />
                  <span className="text-sm text-blue-200/80">Nettoyage automatique activé</span>
                </label>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-300/60">Intervalle :</span>
                  <select
                    value={intervalHours}
                    onChange={(e) => setIntervalHours(Number(e.target.value))}
                    className="bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none"
                    data-testid="cleanup-interval-select"
                  >
                    <option value={1}>Toutes les heures</option>
                    <option value={2}>Toutes les 2 heures</option>
                    <option value={6}>Toutes les 6 heures</option>
                    <option value={12}>Toutes les 12 heures</option>
                    <option value={24}>Toutes les 24 heures</option>
                  </select>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                  data-testid="save-cleanup-settings-btn"
                >
                  {savingSettings ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Sauvegarder
                </Button>
              </div>
            </div>

            {/* Manual cleanup button */}
            <div className="border-t border-blue-500/10 pt-5 flex items-center gap-3">
              <Button
                onClick={handleCleanup}
                disabled={cleaningUp}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="cleanup-btn"
              >
                {cleaningUp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Lancer le nettoyage maintenant
              </Button>
              {cleanupSettings?.pending_cleanup > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-orange-400">
                  <AlertTriangle className="w-4 h-4" />
                  {cleanupSettings.pending_cleanup} fichier(s) expiré(s) en attente
                </span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminStorage;
